import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { defer, from, Observable, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap, timeout } from 'rxjs/operators';
import { NotInputError } from '../errors/not-input.error';
import { OfflineError } from '../errors/offline.error';
import { HttpMethod } from '../interfaces/api-request.interface';
import { API_CLIENT_CONFIG } from '../tokens/api-config.token';
import { AUTH_STRATEGY } from '../tokens/auth-strategy.token';

/**
 * 共通API通信サービス
 *
 * @description
 * Angular HttpClient をラップし、統一的なAPI通信処理を提供します。
 * - トークン管理（AUTH_STRATEGYへの処理委譲による社内リフレッシュ/社外誘導の自動制御）
 * - WISEヘッダー付与（社内・社外共通仕様のマッピングを完全に共用）
 * - エラーハンドリング / タイムアウト監視（120秒）
 * - リトライ処理
 *
 * @example
 * ```typescript
 * // GET リクエスト
 * this.apiClient.request<User>('https://api.example.com/users/1', 'get', 'group1', 'group2')
 *   .subscribe({
 *     next: (data) => console.log(data),
 *     error: (err) => this.handleError(err)
 *   });
 *
 * // POST リクエスト
 * this.apiClient.request<User>('https://api.example.com/users', 'post', 'group1', 'group2',
 *   { name: 'John' }, { timeout: 10000 })
 *   .subscribe(...);
 * ```
 *
 * @author CAP
 */
@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(API_CLIENT_CONFIG);
  private readonly authStrategy = inject(AUTH_STRATEGY);

  /** デフォルトタイムアウト（120秒） */
  private readonly DEFAULT_TIMEOUT = this.config.constTimeout ?? 120000;

  /** デフォルトリトライ回数 */
  private readonly DEFAULT_RETRY_COUNT = 0;

  /** リクエストヘッダー名：Content-Type */
  private readonly REQ_HEADER_NAME_CONTENT_TYPE = 'content-type';
  /** リクエストヘッダー名：Authorization */
  private readonly REQ_HEADER_NAME_AUTHORIZATION = 'Authorization';
  /** リクエストヘッダー名：X_REQUESTED_WITH */
  private readonly REQ_HEADER_NAME_X_REQUESTED_WITH = 'x-requested-with';
  /** リクエストヘッダー名：X_DLCPF_TRACE_ID */
  private readonly REQ_HEADER_NAME_X_DLCPF_TRACE_ID = 'x-dlcpf-trace-id';
  /** リクエストヘッダー名：X_DLCPF_USER_ID */
  private readonly REQ_HEADER_NAME_X_DLCPF_USER_ID = 'x-dlcpf-user-id';
  /** リクエストヘッダー名：X_DLCPF_USER_GROUP1 */
  private readonly REQ_HEADER_NAME_X_DLCPF_USER_GROUP1 = 'x-dlcpf-user-group1';
  /** リクエストヘッダー名：X_DLCPF_USER_GROUP2 */
  private readonly REQ_HEADER_NAME_X_DLCPF_USER_GROUP2 = 'x-dlcpf-user-group2';
  /** リクエストヘッダー値：Content-Type JSON */
  private readonly REQ_HEADER_VALUE_CONTENT_TYPE_JSON = 'application/json; charset=utf-8';
  /** リクエストヘッダー値：X_REQUESTED_WITH */
  private readonly REQ_HEADER_VALUE_X_REQUESTED_WITH = 'DLCPF';

  /**
   * 統一APIリクエストメソッド
   *
   * @template T レスポンスデータの型
   * @param requestUrl URL文字列（必須）
   * @param httpMethod HTTPメソッド（必須）
   * @param group1 所属コード1（必須）WISEヘッダー付与用
   * @param group2 所属コード2（任意）WISEヘッダー付与用
   * @param param リクエストパラメータ（任意）※すべてのメソッドでリクエストボディに設定
   * @param config リクエスト設定（任意）タイムアウト、ヘッダー等
   * @returns Observable<T> レスポンスデータのストリーム
   *
   * @throws {NotInputError} 必須パラメータ未入力時
   * @throws {OfflineError} ネットワーク接続不良時
   * @throws {HttpErrorResponse} HTTPエラー時
   */
  request<T>(requestUrl: string, httpMethod: HttpMethod, userid: string, group1: string, group2?: string, param?: any): Observable<T> {
    console.log(`userid: ${userid},group1: ${group1},group2: ${group2}, httpMethod:${httpMethod.toUpperCase()},requestUrl: ${requestUrl}`);

    // 1. 必須入力チェック
    this.validateRequiredParams(requestUrl, httpMethod, group1);

    // 2. 電波状態チェック
    this.checkOnlineStatus();

    // 3.社内・社外による認証処理を行い、業務APIを実行する
    return defer(() => {
      const isSkipAuth = !!this.config?.skipAuth;
      const timeoutMs = this.config?.timeout ?? this.DEFAULT_TIMEOUT;

      // 【認証不要工程】からのリクエストの場合
      if (isSkipAuth) {
        console.log('🔑 認証不要工程のため、トークン確認をスキップして業務APIを実行します...');
        const headers = this.buildHeaders(userid, group1, group2, this.config?.headers, isSkipAuth);
        // 本来の業務APIを実行
        return this.executeRequest<T>(requestUrl, httpMethod, param, headers, timeoutMs, userid, group1, this.config?.retryCount, group2);
      }

      // 【通常認証工程】からのリクエストの場合
      return from(this.prepareAccessToken()).pipe(
        // 本来の業務APIを実行
        switchMap(() => {
          console.log('🔑 通常認証工程のため、トークン確認を実行して業務APIを実行します...');
          const headers = this.buildHeaders(userid, group1, group2, this.config?.headers, isSkipAuth);
          return this.executeRequest<T>(requestUrl, httpMethod, param, headers, timeoutMs, userid, group1, this.config?.retryCount, group2);
        }),
      );
    });
  }

  /**
   * 必須パラメータ検証
   */
  private validateRequiredParams(requestUrl: string, httpMethod: string, group1: string): void {
    if (!requestUrl || !httpMethod || !group1) {
      throw new NotInputError('URL、HTTPメソッド、ユーザグループ１は必須です');
    }
  }

  /**
   * ネットワーク接続状態チェック
   */
  private checkOnlineStatus(): void {
    if (!navigator.onLine) {
      throw new OfflineError('ネットワーク接続が不良です。通信状態を確認してください。');
    }
  }

  /**
   * アクセストークン確認＆判定メインロジック
   */
  private async prepareAccessToken(): Promise<void> {
    const tokenExists = !!sessionStorage.getItem('access_token');

    if (tokenExists) {
      // 社外アクセス時は期限チェックが「なし」のため、値がなければ常に有効とみなす
      const isCurrentInternal = (this.authStrategy as any).isCurrentlyInternal ?? this.config.isInternal;

      let isExpired = false;

      if (isCurrentInternal) {
        const expiresOnStr = sessionStorage.getItem('expires_on');
        try {
          const hasNotExpired = !!(expiresOnStr && Date.now() > JSON.parse(expiresOnStr));
          isExpired = !hasNotExpired;
        } catch (e) {
          isExpired = true; // エラーが発生した場合は期限切れとみなす
        }
      }

      if (isExpired) {
        console.log('🔑 [バックエンドから401返却] トークンが期限切れのため、再取得/リフレッシュを実行します...');
        // 期限切れの場合、各環境の再取得/誘導処理を実行（具象クラスへ委譲）
        await this.authStrategy.handleTokenRefreshOrRedirect();
      }
    } else {
      console.log('🔑 [バックエンドから401返却] トークンが存在しないため、取得処理を実行します...');
      // 存在しない場合、トークン取得処理を実行（具象クラスへ委譲）
      await this.authStrategy.acquireToken();
    }
  }

  /**
   * HTTPヘッダー構築
   */
  private buildHeaders(userid: string, group1: string, group2?: string, customHeaders?: Record<string, string>, isSkipAuth?: boolean): HttpHeaders {
    const token = sessionStorage.getItem('access_token') || '';
    const csrfHeaderValue = this.config.csrfHeader || this.REQ_HEADER_VALUE_X_REQUESTED_WITH;

    const traceId = this.generateUUID();

    let headers = new HttpHeaders({
      // コンテンツタイプ
      [this.REQ_HEADER_NAME_CONTENT_TYPE]: this.REQ_HEADER_VALUE_CONTENT_TYPE_JSON,
      // CSRF対策項目
      [this.REQ_HEADER_NAME_X_REQUESTED_WITH]: csrfHeaderValue,
      // トレースID
      [this.REQ_HEADER_NAME_X_DLCPF_TRACE_ID]: traceId,
      // ユーザID
      [this.REQ_HEADER_NAME_X_DLCPF_USER_ID]: userid,
      // ユーザグループ１
      [this.REQ_HEADER_NAME_X_DLCPF_USER_GROUP1]: group1,
    });

    // 認証不要工程以外、かつトークンが存在する場合のみ正しくAuthorizationを付与
    if (!isSkipAuth && token) {
      // アクセストークン
      headers = headers.set(this.REQ_HEADER_NAME_AUTHORIZATION, `Bearer ${token}`);
    }

    if (group2) {
      // ユーザグループ２（任意）
      headers = headers.set(this.REQ_HEADER_NAME_X_DLCPF_USER_GROUP2, group2 || '');
    }

    if (customHeaders) {
      Object.entries(customHeaders).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
    }

    console.log(
      'HTTPヘッダー構築完了:',
      headers.keys().map((key) => `${key}: ${headers.get(key)}`),
    );
    return headers;
  }

  /**
   * HTTPリクエスト実行
   */
  private executeRequest<T>(
    url: string,
    method: HttpMethod,
    body: any,
    headers: HttpHeaders,
    timeoutMs: number,
    userid: string,
    group1: string,
    retryCount?: number,
    group2?: string,
  ): Observable<T> {
    const getOptions = (currentHeaders: HttpHeaders) => ({ headers: currentHeaders, body });

    const createHttpObservable = (currentHeaders: HttpHeaders): Observable<T> => {
      const options = getOptions(currentHeaders);

      switch (method.toLowerCase()) {
        case 'get':
          return this.http.get<T>(url, options);
        case 'post':
          return this.http.post<T>(url, body, { headers });
        case 'put':
          return this.http.put<T>(url, body, { headers });
        case 'delete':
          return this.http.delete<T>(url, options);
        default:
          throw new Error(`サポートされていないHTTPメソッド: ${method}`);
      }
    };

    return createHttpObservable(headers).pipe(
      timeout(timeoutMs),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          console.warn('🔑 [バックエンドから401返却] サーバー側でTokenが無効化されたため、無効化処理またはリダイレクトを実行します...');

          // 実行戦略：社内呼び出しの場合は /.auth/refresh でリフレッシュ、社外の場合は WISE ログイン画面へ遷移
          return from(this.authStrategy.handleTokenRefreshOrRedirect()).pipe(
            switchMap(() => {
              console.log('🔄 Tokenの再取得/リフレッシュが成功しました。元のAPIリクエストを再試行します...');
              // 最新の Token を含んだ Headers を再構築
              const newHeaders = this.buildHeaders(userid, group1, group2, this.config?.headers, false);
              // 最新の Headers を使用してリクエストを再実行し、現在の 401 インターセプトから抜ける
              return createHttpObservable(newHeaders);
            }),
          );
        }
        // 401 以外のエラー（500、0 など）の場合は、そのままエラーをスローし、後続の retry または handleError に処理を委ねる
        return throwError(() => error);
      }),
      retry({
        count: retryCount ?? this.DEFAULT_RETRY_COUNT,
        delay: (error, retryAttempt) => {
          if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 999)) {
            return throwError(() => error); // 401 および開閉局エラー（999）は、無駄な再試行を避けるため即座にエラーをスロー
          }
          const delayMs = Math.pow(2, retryAttempt) * 1000;
          console.warn(`リトライ ${retryAttempt}回目 (${delayMs}ms後)`, error);
          return timer(delayMs);
        },
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  /**
   * エラーハンドリング
   */
  private handleError(error: any): Observable<never> {
    if (error.name === 'TimeoutError') {
      console.error('⏱️ リクエストタイムアウト', error);
      return throwError(() => new Error('リクエストがタイムアウトしました'));
    }

    if (error instanceof HttpErrorResponse && error.status === 0) {
      console.error('🔌 ネットワークエラー', error);
      return throwError(() => new OfflineError('ネットワーク接続が不良です'));
    }

    if (error instanceof HttpErrorResponse) {
      console.error(`❌ HTTPエラー [${error.status}]`, error);

      if (error.status === 401) {
        console.warn('🔑 トークン期限切れ。リフレッシュ処理を実行してください。');
      }

      if (error.status === 999) {
        console.warn('🚪 開閉局エラー');
      }

      return throwError(() => error);
    }

    console.error('⚠️ 予期しないエラー', error);
    return throwError(() => error);
  }

  /**
   * 🌟 UUID (v4) を生成するヘルパーメソッド
   *
   */
  private generateUUID(): string {
    // ブラウザの crypto API があれば安全に乱数を生成して16バイト -> 32桁16進数に変換
    if (typeof crypto !== 'undefined' && (crypto as any).getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }

    // フォールバック: Math.random を使って32桁の16進数を生成
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += Math.floor(Math.random() * 16).toString(16);
    }
    return result;
  }
}
